import {createSketchSolverIslandId, sortUniqueStrings} from './SolverIslandSnapshot';

/**
 * solver island 图中的实体连接边。
 */
export interface SolverIslandGraphEntityLink {
    /** 草图实体稳定 ID。 */
    readonly entityId: string;
    /** 实体包含或引用的点 ID。 */
    readonly pointIds?: readonly string[];
}

/**
 * solver island 图中的约束连接边。
 *
 * 设计说明：
 * 约束会把多个草图实体、点与尺寸绑到同一个求解连通分量。历史系统必须按连通分量采集 before/after，
 * 否则 undo 后只恢复直接实体会丢失被 solver 推动的间接实体。
 */
export interface SolverIslandGraphConstraintLink {
    /** 约束稳定 ID。 */
    readonly constraintId: string;
    /** 约束直接作用的草图实体 ID。 */
    readonly entityIds: readonly string[];
    /** 约束关联的尺寸 ID。 */
    readonly dimensionIds?: readonly string[];
    /** 约束关联的点 ID。 */
    readonly pointIds?: readonly string[];
}

/**
 * solver island 图中的尺寸连接边。
 */
export interface SolverIslandGraphDimensionLink {
    /** 尺寸稳定 ID。 */
    readonly dimensionId: string;
    /** 尺寸直接作用的草图实体 ID。 */
    readonly entityIds: readonly string[];
    /** 尺寸关联的约束 ID。 */
    readonly constraintIds?: readonly string[];
    /** 尺寸关联的点 ID。 */
    readonly pointIds?: readonly string[];
}

/**
 * solver island 解析输入。
 */
export interface ResolveSolverIslandInput {
    /** 草图稳定 ID。 */
    readonly sketchId: string;
    /** 触发操作的实体 ID。 */
    readonly seedEntityIds?: readonly string[];
    /** 触发操作的点 ID。 */
    readonly seedPointIds?: readonly string[];
    /** 触发操作的约束 ID。 */
    readonly seedConstraintIds?: readonly string[];
    /** 触发操作的尺寸 ID。 */
    readonly seedDimensionIds?: readonly string[];
    /** 当前草图约束连接边。 */
    readonly constraints: readonly SolverIslandGraphConstraintLink[];
    /** 当前草图尺寸连接边。 */
    readonly dimensions?: readonly SolverIslandGraphDimensionLink[];
    /** 当前草图实体到点的连接边。 */
    readonly entities?: readonly SolverIslandGraphEntityLink[];
    /** 显式纳入 island 的实体 ID，例如拖拽过程中 solver 还未建立完整约束边时的临时目标。 */
    readonly explicitEntityIds?: readonly string[];
    /** 显式纳入 island 的点 ID，例如多段线或样条绘制过程中的活动控制点。 */
    readonly explicitPointIds?: readonly string[];
    /** 外部指定的稳定影响域 ID；新增 / 删除关系时可由 before/after union scope 生成。 */
    readonly islandId?: string;
    /** 用于生成稳定影响域 ID 的成员 ID；不传时使用解析出的当前成员集合。 */
    readonly identityMemberIds?: readonly string[];
}

/**
 * solver island 解析结果。
 */
export interface ResolvedSolverIsland {
    /** solver island 稳定 ID。 */
    readonly islandId: string;
    /** 草图稳定 ID。 */
    readonly sketchId: string;
    /** island 内实体 ID。 */
    readonly entityIds: readonly string[];
    /** island 内点 ID。 */
    readonly pointIds: readonly string[];
    /** island 内约束 ID。 */
    readonly constraintIds: readonly string[];
    /** island 内尺寸 ID。 */
    readonly dimensionIds: readonly string[];
}

/**
 * 可变 island 解析状态。
 */
interface MutableSolverIslandResolution {
    /** 待访问实体 ID。 */
    readonly entityQueue: string[];
    /** 待访问点 ID。 */
    readonly pointQueue: string[];
    /** 待访问约束 ID。 */
    readonly constraintQueue: string[];
    /** 待访问尺寸 ID。 */
    readonly dimensionQueue: string[];
    /** 已纳入实体 ID。 */
    readonly entityIds: Set<string>;
    /** 已纳入点 ID。 */
    readonly pointIds: Set<string>;
    /** 已纳入约束 ID。 */
    readonly constraintIds: Set<string>;
    /** 已纳入尺寸 ID。 */
    readonly dimensionIds: Set<string>;
}

/**
 * 建立 ID 到约束连接边的索引。
 *
 * @param constraints 约束连接边。
 * @returns 约束索引。
 */
function createConstraintIndex(
    constraints: readonly SolverIslandGraphConstraintLink[],
): Map<string, SolverIslandGraphConstraintLink> {
    return new Map(constraints.map((constraint) => [constraint.constraintId, constraint]));
}

/**
 * 建立 ID 到尺寸连接边的索引。
 *
 * @param dimensions 尺寸连接边。
 * @returns 尺寸索引。
 */
function createDimensionIndex(
    dimensions: readonly SolverIslandGraphDimensionLink[],
): Map<string, SolverIslandGraphDimensionLink> {
    return new Map(dimensions.map((dimension) => [dimension.dimensionId, dimension]));
}

/**
 * 建立实体到点 ID 的索引。
 *
 * @param entities 实体连接边。
 * @returns 实体到点 ID 的索引。
 */
function createEntityToPointIndex(
    entities: readonly SolverIslandGraphEntityLink[],
): Map<string, readonly string[]> {
    return new Map(entities.map((entity) => [entity.entityId, sortUniqueStrings(entity.pointIds ?? [])]));
}

/**
 * 建立点到实体 ID 的反向索引。
 *
 * @param entities 实体连接边。
 * @returns 点到实体 ID 的反向索引。
 */
function createPointToEntityIndex(
    entities: readonly SolverIslandGraphEntityLink[],
): Map<string, readonly string[]> {
    const index = new Map<string, string[]>();

    entities.forEach((entity) => {
        (entity.pointIds ?? []).forEach((pointId) => {
            const existingIds = index.get(pointId) ?? [];

            if (!existingIds.includes(entity.entityId)) {
                existingIds.push(entity.entityId);
            }

            index.set(pointId, existingIds);
        });
    });

    return index;
}

/**
 * 建立实体到约束 ID 的反向索引。
 *
 * @param constraints 约束连接边。
 * @returns 实体到约束 ID 的反向索引。
 */
function createEntityToConstraintIndex(
    constraints: readonly SolverIslandGraphConstraintLink[],
): Map<string, readonly string[]> {
    const index = new Map<string, string[]>();

    constraints.forEach((constraint) => {
        constraint.entityIds.forEach((entityId) => {
            const existingIds = index.get(entityId) ?? [];

            // 这里保留重复保护，避免上游重复边导致 BFS 做无意义工作。
            if (!existingIds.includes(constraint.constraintId)) {
                existingIds.push(constraint.constraintId);
            }

            index.set(entityId, existingIds);
        });
    });

    return index;
}

/**
 * 建立点到约束 ID 的反向索引。
 *
 * @param constraints 约束连接边。
 * @returns 点到约束 ID 的反向索引。
 */
function createPointToConstraintIndex(
    constraints: readonly SolverIslandGraphConstraintLink[],
): Map<string, readonly string[]> {
    const index = new Map<string, string[]>();

    constraints.forEach((constraint) => {
        (constraint.pointIds ?? []).forEach((pointId) => {
            const existingIds = index.get(pointId) ?? [];

            if (!existingIds.includes(constraint.constraintId)) {
                existingIds.push(constraint.constraintId);
            }

            index.set(pointId, existingIds);
        });
    });

    return index;
}

/**
 * 建立实体到尺寸 ID 的反向索引。
 *
 * @param dimensions 尺寸连接边。
 * @returns 实体到尺寸 ID 的反向索引。
 */
function createEntityToDimensionIndex(
    dimensions: readonly SolverIslandGraphDimensionLink[],
): Map<string, readonly string[]> {
    const index = new Map<string, string[]>();

    dimensions.forEach((dimension) => {
        dimension.entityIds.forEach((entityId) => {
            const existingIds = index.get(entityId) ?? [];

            // 尺寸同样可能由多个选择目标生成，反向索引必须去重。
            if (!existingIds.includes(dimension.dimensionId)) {
                existingIds.push(dimension.dimensionId);
            }

            index.set(entityId, existingIds);
        });
    });

    return index;
}

/**
 * 建立点到尺寸 ID 的反向索引。
 *
 * @param dimensions 尺寸连接边。
 * @returns 点到尺寸 ID 的反向索引。
 */
function createPointToDimensionIndex(
    dimensions: readonly SolverIslandGraphDimensionLink[],
): Map<string, readonly string[]> {
    const index = new Map<string, string[]>();

    dimensions.forEach((dimension) => {
        (dimension.pointIds ?? []).forEach((pointId) => {
            const existingIds = index.get(pointId) ?? [];

            if (!existingIds.includes(dimension.dimensionId)) {
                existingIds.push(dimension.dimensionId);
            }

            index.set(pointId, existingIds);
        });
    });

    return index;
}

/**
 * 将字符串列表推入队列。
 *
 * @param queue 目标队列。
 * @param ids 待推入 ID。
 */
function enqueueIds(queue: string[], ids: readonly string[] | undefined): void {
    if (!ids) {
        return;
    }

    ids.forEach((id) => {
        queue.push(id);
    });
}

/**
 * 访问实体并展开关联约束与尺寸。
 *
 * @param resolution 可变解析状态。
 * @param entityToConstraintIndex 实体到约束 ID 的索引。
 * @param entityToDimensionIndex 实体到尺寸 ID 的索引。
 */
function visitNextEntity(
    resolution: MutableSolverIslandResolution,
    entityToPointIndex: Map<string, readonly string[]>,
    entityToConstraintIndex: Map<string, readonly string[]>,
    entityToDimensionIndex: Map<string, readonly string[]>,
): void {
    const entityId = resolution.entityQueue.shift();

    if (!entityId || resolution.entityIds.has(entityId)) {
        return;
    }

    resolution.entityIds.add(entityId);
    enqueueIds(resolution.pointQueue, entityToPointIndex.get(entityId));
    enqueueIds(resolution.constraintQueue, entityToConstraintIndex.get(entityId));
    enqueueIds(resolution.dimensionQueue, entityToDimensionIndex.get(entityId));
}

/**
 * 访问点并展开关联实体、约束与尺寸。
 *
 * @param resolution 可变解析状态。
 * @param pointToEntityIndex 点到实体 ID 的索引。
 * @param pointToConstraintIndex 点到约束 ID 的索引。
 * @param pointToDimensionIndex 点到尺寸 ID 的索引。
 */
function visitNextPoint(
    resolution: MutableSolverIslandResolution,
    pointToEntityIndex: Map<string, readonly string[]>,
    pointToConstraintIndex: Map<string, readonly string[]>,
    pointToDimensionIndex: Map<string, readonly string[]>,
): void {
    const pointId = resolution.pointQueue.shift();

    if (!pointId || resolution.pointIds.has(pointId)) {
        return;
    }

    resolution.pointIds.add(pointId);
    enqueueIds(resolution.entityQueue, pointToEntityIndex.get(pointId));
    enqueueIds(resolution.constraintQueue, pointToConstraintIndex.get(pointId));
    enqueueIds(resolution.dimensionQueue, pointToDimensionIndex.get(pointId));
}

/**
 * 访问约束并展开关联实体与尺寸。
 *
 * @param resolution 可变解析状态。
 * @param constraintIndex 约束索引。
 */
function visitNextConstraint(
    resolution: MutableSolverIslandResolution,
    constraintIndex: Map<string, SolverIslandGraphConstraintLink>,
): void {
    const constraintId = resolution.constraintQueue.shift();

    if (!constraintId || resolution.constraintIds.has(constraintId)) {
        return;
    }

    const constraint = constraintIndex.get(constraintId);

    if (!constraint) {
        return;
    }

    resolution.constraintIds.add(constraintId);
    enqueueIds(resolution.entityQueue, constraint.entityIds);
    enqueueIds(resolution.pointQueue, constraint.pointIds);
    enqueueIds(resolution.dimensionQueue, constraint.dimensionIds);
}

/**
 * 访问尺寸并展开关联实体与约束。
 *
 * @param resolution 可变解析状态。
 * @param dimensionIndex 尺寸索引。
 */
function visitNextDimension(
    resolution: MutableSolverIslandResolution,
    dimensionIndex: Map<string, SolverIslandGraphDimensionLink>,
): void {
    const dimensionId = resolution.dimensionQueue.shift();

    if (!dimensionId || resolution.dimensionIds.has(dimensionId)) {
        return;
    }

    const dimension = dimensionIndex.get(dimensionId);

    if (!dimension) {
        return;
    }

    resolution.dimensionIds.add(dimensionId);
    enqueueIds(resolution.entityQueue, dimension.entityIds);
    enqueueIds(resolution.pointQueue, dimension.pointIds);
    enqueueIds(resolution.constraintQueue, dimension.constraintIds);
}

/**
 * 判断 BFS 队列是否仍有待访问节点。
 *
 * @param resolution 可变解析状态。
 * @returns 是否仍有待访问节点。
 */
function hasPendingNodes(resolution: MutableSolverIslandResolution): boolean {
    return resolution.entityQueue.length > 0
        || resolution.pointQueue.length > 0
        || resolution.constraintQueue.length > 0
        || resolution.dimensionQueue.length > 0;
}

/**
 * 解析草图 solver island。
 *
 * @param input island 解析输入。
 * @returns solver island 解析结果。
 */
export function resolveSolverIsland(input: ResolveSolverIslandInput): ResolvedSolverIsland {
    const constraints = input.constraints;
    const dimensions = input.dimensions ?? [];
    const entities = input.entities ?? [];
    const constraintIndex = createConstraintIndex(constraints);
    const dimensionIndex = createDimensionIndex(dimensions);
    const entityToPointIndex = createEntityToPointIndex(entities);
    const pointToEntityIndex = createPointToEntityIndex(entities);
    const entityToConstraintIndex = createEntityToConstraintIndex(constraints);
    const entityToDimensionIndex = createEntityToDimensionIndex(dimensions);
    const pointToConstraintIndex = createPointToConstraintIndex(constraints);
    const pointToDimensionIndex = createPointToDimensionIndex(dimensions);
    const resolution: MutableSolverIslandResolution = {
        entityQueue: [
            ...(input.seedEntityIds ?? []),
            ...(input.explicitEntityIds ?? []),
        ],
        pointQueue: [
            ...(input.seedPointIds ?? []),
            ...(input.explicitPointIds ?? []),
        ],
        constraintQueue: [...(input.seedConstraintIds ?? [])],
        dimensionQueue: [...(input.seedDimensionIds ?? [])],
        entityIds: new Set(),
        pointIds: new Set(),
        constraintIds: new Set(),
        dimensionIds: new Set(),
    };

    while (hasPendingNodes(resolution)) {
        // 访问顺序固定为实体 -> 点 -> 约束 -> 尺寸，保证同一输入产生稳定结果。
        visitNextEntity(
            resolution,
            entityToPointIndex,
            entityToConstraintIndex,
            entityToDimensionIndex,
        );
        visitNextPoint(
            resolution,
            pointToEntityIndex,
            pointToConstraintIndex,
            pointToDimensionIndex,
        );
        visitNextConstraint(resolution, constraintIndex);
        visitNextDimension(resolution, dimensionIndex);
    }

    const entityIds = sortUniqueStrings(Array.from(resolution.entityIds));
    const pointIds = sortUniqueStrings(Array.from(resolution.pointIds));
    const constraintIds = sortUniqueStrings(Array.from(resolution.constraintIds));
    const dimensionIds = sortUniqueStrings(Array.from(resolution.dimensionIds));
    const islandMemberIds = [
        ...entityIds,
        ...pointIds,
        ...constraintIds,
        ...dimensionIds,
    ];
    const identityMemberIds = input.identityMemberIds ?? islandMemberIds;
    const islandId = input.islandId ?? createSketchSolverIslandId(input.sketchId, identityMemberIds);

    return {
        islandId,
        sketchId: input.sketchId,
        entityIds,
        pointIds,
        constraintIds,
        dimensionIds,
    };
}
